import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Menu, X, User, LogOut, LayoutDashboard, CreditCard, AlertTriangle, Bell, MessageCircle } from 'lucide-react';
import Chatbot from '@/components/Chatbot';
import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { getUnreadCount, getNotifications, markNotificationsRead } from '@/lib/api';
import { logger } from '@/lib/logger';

// Scroll to top on every route change
const useScrollToTopOnRouteChange = () => {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname]);
};

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hideNav, setHideNav] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);

  useScrollToTopOnRouteChange();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isLandingPage = location.pathname === '/';

  // Track scroll position
  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 0);

      // Find the "hide trigger" anchor (e.g., "Sådan fungerer det" section on landing)
      const anchor = document.getElementById('nav-hide-trigger');
      if (anchor) {
        const rect = anchor.getBoundingClientRect();
        // Hide nav ~2cm (~75px) BEFORE the nav (~80px tall) would reach the heading.
        // i.e., trigger when the heading top is ≤ ~155px from viewport top.
        const HEADER_OFFSET = 155;
        if (rect.top <= HEADER_OFFSET && y > lastScrollY) {
          setHideNav(true);
        } else if (rect.top > HEADER_OFFSET + 60 || y < lastScrollY) {
          setHideNav(false);
        }
      } else {
        // Fallback: generic hide-on-scroll-down behaviour for other pages
        if (y > 200 && y > lastScrollY) setHideNav(true);
        else if (y < lastScrollY || y <= 80) setHideNav(false);
      }
      setLastScrollY(y);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  // Real-time-feel notifications: poll every 10s, show popup toasts for new ones
  const seenNotifIds = useRef(new Set());
  const firstLoad = useRef(true);

  useEffect(() => {
    if (!user || user.notifications_enabled === false) return;
    let cancelled = false;

    const poll = async () => {
      try {
        const [countRes, listRes] = await Promise.all([getUnreadCount(), getNotifications()]);
        if (cancelled) return;
        setUnreadCount(countRes.data.count);
        const notifs = listRes.data || [];
        setNotifications(notifs);

        // Show toasts for newly-arrived UNREAD items (skip first load to avoid noise)
        if (!firstLoad.current) {
          const newOnes = notifs.filter(n => !n.read && !seenNotifIds.current.has(n.id));
          newOnes.slice(0, 3).forEach(n => {
            toast(n.message, {
              description: 'Vibra',
              duration: 6000,
              action: n.link ? {
                label: 'Åbn',
                onClick: () => navigate(n.link),
              } : undefined,
            });
          });
        }
        notifs.forEach(n => seenNotifIds.current.add(n.id));
        firstLoad.current = false;
      } catch (e) { logger.warn('notifications poll failed', e); }
    };

    poll();
    const interval = setInterval(poll, 10000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [user, navigate]);

  const handleOpenNotifs = async () => {
    setShowNotifs(!showNotifs);
    if (!showNotifs) {
      try {
        const res = await getNotifications();
        setNotifications(res.data);
        await markNotificationsRead();
        setUnreadCount(0);
      } catch (e) { logger.error('mark notifications read failed', e); }
    }
  };

  // Determine nav colors based on scroll position and page
  const navBgClass = isLandingPage 
    ? ((scrolled || mobileMenuOpen) ? 'bg-white/95 backdrop-blur-md border-b border-border' : 'bg-transparent')
    : 'bg-white/95 backdrop-blur-md border-b border-border';
  
  const isLight = (isLandingPage && !scrolled && !mobileMenuOpen) ? false : true;
  
  const textColorClass = isLight ? 'text-foreground' : 'text-white';
  const linkColorClass = isLight ? 'text-muted-foreground hover:text-foreground' : 'text-white/90 hover:text-white';
  const buttonClass = !isLight 
    ? 'border-white/30 text-white hover:bg-white/10' 
    : '';
  const primaryButtonClass = !isLight 
    ? 'bg-white text-primary hover:bg-white/90' 
    : 'bg-primary text-primary-foreground';

  return (
    <div className="min-h-screen flex flex-col">
      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${navBgClass} ${hideNav && !mobileMenuOpen ? '-translate-y-full opacity-0 pointer-events-none' : 'translate-y-0 opacity-100'}`}>
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo — both variants are rendered and cross-fade in sync with the header bg
                to avoid a mid-scroll flash where the wrong logo sits on the wrong background. */}
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <span className="relative inline-block h-10 aspect-square">
                {/* White state: simple inverted logo */}
                <img
                  src="/logo.png"
                  alt="Vibra"
                  className={`absolute inset-0 h-full w-full object-contain brightness-0 invert transition-opacity duration-300 ${isLight ? 'opacity-0' : 'opacity-100'}`}
                />
                {/* Bronze state: layered green + shimmering bronze (mask) */}
                <span
                  className={`absolute inset-0 transition-opacity duration-300 ${isLight ? 'opacity-100' : 'opacity-0'}`}
                  aria-hidden={!isLight}
                >
                  <img
                    src="/logo-green.png"
                    alt=""
                    className="absolute inset-0 h-full w-full object-contain"
                  />
                  <span
                    className="absolute inset-0 logo-bronze-shimmer pointer-events-none"
                    style={{
                      WebkitMaskImage: 'url(/logo-bronze-mask.png)',
                      maskImage: 'url(/logo-bronze-mask.png)',
                    }}
                    aria-hidden="true"
                  />
                </span>
              </span>
              <span className="text-xl font-semibold tracking-tight relative">
                {/* Two stacked variants cross-fade like the logo so the swap is smooth (no
                    text-fill-color/gradient flash when isLight flips during scroll). */}
                <span className={`transition-opacity duration-300 ${isLight ? 'opacity-0' : 'opacity-100'} text-white`}>
                  Vibra
                </span>
                <span
                  className={`absolute inset-0 transition-opacity duration-300 bronze-text ${isLight ? 'opacity-100' : 'opacity-0'}`}
                  aria-hidden={!isLight}
                >
                  Vibra
                </span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <Link 
                to="/directory" 
                className={`font-medium nav-link transition-colors duration-300 ${linkColorClass}`}
                data-testid="nav-directory"
              >
                Filtrer & sortér
              </Link>
              {user?.user_type !== 'user' && (
                <Link 
                  to="/abonnement" 
                  className={`font-medium nav-link transition-colors duration-300 ${linkColorClass}`}
                  data-testid="nav-pricing"
                >
                  Abonnement
                </Link>
              )}
              
              {user ? (
                <div className="flex items-center gap-3">
                  {/* Notification bell (always visible when logged in) */}
                  <div className="relative">
                      <Button
                        variant="ghost"
                        size="icon"
                        className={`relative ${buttonClass}`}
                        onClick={handleOpenNotifs}
                        data-testid="notification-bell"
                        aria-label="Notifikationer"
                      >
                        <Bell className="w-5 h-5" />
                        {unreadCount > 0 && (
                          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center leading-none" data-testid="unread-badge">
                            {unreadCount > 99 ? '99+' : unreadCount}
                          </span>
                        )}
                      </Button>
                      {showNotifs && (
                        <div className="absolute right-0 top-12 w-80 bg-white rounded-xl shadow-xl border z-50 max-h-96 overflow-y-auto" data-testid="notifications-dropdown">
                          <div className="p-3 border-b font-semibold text-sm">Notifikationer</div>
                          {notifications.length > 0 ? notifications.slice(0, 10).map((n) => (
                            <div key={n.id} className={`p-3 border-b text-sm hover:bg-muted/50 cursor-pointer ${!n.read ? 'bg-primary/5' : ''}`} 
                                 onClick={() => {
                                   if (n.link) navigate(n.link);
                                   else if (n.company_id) navigate(`/company/${n.company_id}`);
                                   setShowNotifs(false);
                                 }}
                                 data-testid={`notif-${n.id}`}>
                              <p>{n.message}</p>
                              <p className="text-xs text-muted-foreground mt-1">{new Date(n.created_at).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                            </div>
                          )) : (
                            <div className="p-4 text-center text-sm text-muted-foreground">Ingen notifikationer</div>
                          )}
                        </div>
                      )}
                    </div>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant={!isLight ? "outline" : "ghost"} 
                      className={`gap-2 transition-colors duration-300 ${buttonClass}`}
                      data-testid="user-menu-trigger"
                    >
                      <User className="w-4 h-4" />
                      {user.name || user.company_name}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem onClick={() => navigate(user.user_type === 'company' ? '/company-dashboard' : '/dashboard')} data-testid="menu-dashboard">
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                      Profil
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/messages')} data-testid="menu-messages">
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Beskeder
                    </DropdownMenuItem>
                    {user.user_type !== 'user' && (
                      <DropdownMenuItem onClick={() => navigate('/abonnement')} data-testid="menu-subscription">
                        <CreditCard className="w-4 h-4 mr-2" />
                        Abonnement
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="menu-logout">
                      <LogOut className="w-4 h-4 mr-2" />
                      Log ud
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <Button 
                    variant="ghost" 
                    onClick={() => navigate('/login')}
                    className={`transition-colors duration-300 ${!isLight ? 'text-white hover:bg-white/10' : ''}`}
                    data-testid="nav-login"
                  >
                    Log ind
                  </Button>
                  <Button 
                    onClick={() => navigate('/register')}
                    className={`transition-colors duration-300 ${primaryButtonClass}`}
                    data-testid="nav-register"
                  >
                    Opret konto
                  </Button>
                </div>
              )}
            </div>

            {/* Mobile: Bell + Menu */}
            <div className="md:hidden flex items-center gap-1">
              {user && (
                <div className="relative">
                  <Button
                    variant="ghost"
                    size="icon"
                    className={`relative ${buttonClass}`}
                    onClick={handleOpenNotifs}
                    data-testid="notification-bell-mobile"
                    aria-label="Notifikationer"
                  >
                    <Bell className="w-5 h-5" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center leading-none" data-testid="unread-badge-mobile">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                  </Button>
                  {showNotifs && (
                    <div className="absolute right-0 top-12 w-72 bg-white rounded-xl shadow-xl border z-50 max-h-96 overflow-y-auto">
                      <div className="p-3 border-b font-semibold text-sm">Notifikationer</div>
                      {notifications.length > 0 ? notifications.slice(0, 10).map((n) => (
                        <div key={n.id} className={`p-3 border-b text-sm hover:bg-muted/50 cursor-pointer ${!n.read ? 'bg-primary/5' : ''}`}
                             onClick={() => {
                               if (n.link) navigate(n.link);
                               else if (n.company_id) navigate(`/company/${n.company_id}`);
                               setShowNotifs(false);
                             }}>
                          <p>{n.message}</p>
                          <p className="text-xs text-muted-foreground mt-1">{new Date(n.created_at).toLocaleDateString('da-DK', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                        </div>
                      )) : (
                        <div className="p-4 text-center text-sm text-muted-foreground">Ingen notifikationer</div>
                      )}
                    </div>
                  )}
                </div>
              )}
              <button
                className="p-2"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? (
                  <X className={`w-6 h-6 transition-colors duration-300 ${textColorClass}`} />
                ) : (
                  <Menu className={`w-6 h-6 transition-colors duration-300 ${textColorClass}`} />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className={`md:hidden py-4 border-t ${!isLight ? 'border-white/20' : 'border-border'}`}>
              <div className="flex flex-col gap-4">
                <Link 
                  to="/directory" 
                  className={`font-medium py-2 ${textColorClass}`}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-nav-directory"
                >
                  Filtrer & sortér
                </Link>
                {user?.user_type !== 'user' && (
                  <Link 
                    to="/abonnement" 
                    className={`font-medium py-2 ${textColorClass}`}
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid="mobile-nav-pricing"
                  >
                    Abonnement
                  </Link>
                )}
                {user ? (
                  <>
                    <Link 
                      to={user.user_type === 'company' ? '/company-dashboard' : '/dashboard'}
                      className={`font-medium py-2 ${textColorClass}`}
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid="mobile-nav-dashboard"
                    >
                      Profil
                    </Link>
                    <button 
                      onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                      className={`font-medium py-2 text-left ${!isLight ? 'text-white' : 'text-destructive'}`}
                      data-testid="mobile-nav-logout"
                    >
                      Log ud
                    </button>
                  </>
                ) : (
                  <div className="flex flex-col gap-2 pt-2">
                    <Button 
                      variant="outline" 
                      onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}
                      className={!isLight ? 'border-white text-white hover:bg-white/10' : ''}
                      data-testid="mobile-nav-login"
                    >
                      Log ind
                    </Button>
                    <Button 
                      onClick={() => { navigate('/register'); setMobileMenuOpen(false); }}
                      className={!isLight ? 'bg-white text-primary' : ''}
                      data-testid="mobile-nav-register"
                    >
                      Opret konto
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Spacer for fixed nav on non-landing pages */}
      {!isLandingPage && <div className="h-16 md:h-20" />}

      {/* Trial/Subscription Banner */}
      {user && !isLandingPage && (
        <>
          {user.trial_active && user.trial_days_left > 0 && user.subscription_tier === 'none' && (
            <div className="bg-[#faf3ec] border-b border-[#e8c4a8] px-4 py-2.5 text-center" data-testid="trial-banner">
              <p className="text-sm text-[#6b3e25]">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                Din prøveperiode udløber om <strong>{user.trial_days_left} dage</strong>.{' '}
                <button 
                  onClick={() => navigate('/abonnement')} 
                  className="underline font-semibold hover:text-[#4a2a18]"
                >
                  Vælg et abonnement
                </button>
              </p>
            </div>
          )}
          {!user.has_access && !user.trial_active && user.subscription_tier === 'none' && user.stripe_customer_id && (
            <div className="bg-red-50 border-b border-red-200 px-4 py-2.5 text-center" data-testid="expired-banner">
              <p className="text-sm text-red-800">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                Din prøveperiode er udløbet.{' '}
                <button 
                  onClick={() => navigate('/abonnement')} 
                  className="underline font-semibold hover:text-red-900"
                >
                  Abonner nu for at fortsætte
                </button>
              </p>
            </div>
          )}
        </>
      )}

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      {!isLandingPage && (
        <footer className="bg-primary text-primary-foreground py-12">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <img src="/logo.png" alt="Vibra" className="h-5 w-auto object-contain brightness-0 invert" />
                  <h4 className="font-semibold text-white">Vibra</h4>
                </div>
                <p className="text-white/70 text-sm">
                  Find de bedste håndværkere i Danmark
                </p>
              </div>
              <div>
                <h4 className="font-semibold mb-4">Links</h4>
                <ul className="space-y-2 text-sm text-white/70">
                  <li><Link to="/directory" className="hover:text-white">Find Håndværker</Link></li>
                  <li><Link to="/abonnement" className="hover:text-white">Abonnement</Link></li>
                  <li><Link to="/register" className="hover:text-white">Opret konto</Link></li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-4">Kontakt</h4>
                <ul className="space-y-2 text-sm text-white/70">
                  <li>kontakt@vibra.dk</li>
                  <li>CVR-nr: 46663772</li>
                  <li>vibra.dk</li>
                  <li className="pt-1 text-white/50 text-xs">
                    Mail svartider: Fre-Søn 14:00-18:00
                  </li>
                </ul>
              </div>
            </div>
            <div className="border-t border-white/20 mt-8 pt-8 text-center text-sm text-white/50">
              © 2026 Vibra. Alle rettigheder forbeholdes.
            </div>
          </div>
        </footer>
      )}

      {/* AI Chatbot */}
      <Chatbot />
    </div>
  );
};

export default Layout;
